import React, { useLayoutEffect, useRef } from "react"
import { Button } from "../button"
import { Icon } from "../icon"
import { useTrackScrollOverflow } from "../use-track-scroll-overflow"
import { allowsMotion, cn } from "../utils"
import type { TabsBaseOrientation } from "./tabs-base-styles"

// =============================================================================
// Styles
// =============================================================================

const tabsBaseScrollButtonContainerStyles =
  "absolute inset-y-0 flex items-center"

// =============================================================================
// Helpers
// =============================================================================

type ScrollDirection = "start" | "end"

function getScrollPadding(scroller: HTMLElement) {
  const { scrollPaddingLeft, scrollPaddingRight } = getComputedStyle(scroller)
  return {
    left: parseFloat(scrollPaddingLeft) || 0,
    right: parseFloat(scrollPaddingRight) || 0
  }
}

// Scrolls only the scroller, unlike `scrollIntoView`, which would also scroll
// the page to the scroller on load
function scrollItemClearOfFades(scroller: HTMLElement, item: Element) {
  const scrollPadding = getScrollPadding(scroller)
  const scrollerRect = scroller.getBoundingClientRect()
  const itemRect = item.getBoundingClientRect()
  const hiddenLeft = scrollerRect.left + scrollPadding.left - itemRect.left
  const hiddenRight =
    itemRect.right - (scrollerRect.right - scrollPadding.right)

  if (hiddenLeft > 0) scroller.scrollLeft -= hiddenLeft
  else if (hiddenRight > 0) scroller.scrollLeft += hiddenRight
}

// Scrolls by the width between the fades, so the item cut off by one fade
// becomes the first fully visible item. Narrow containers, where the fades
// leave little room, still scroll by half their width.
function scrollByPage(scroller: HTMLElement, direction: ScrollDirection) {
  const scrollPadding = getScrollPadding(scroller)
  const widthBetweenFades =
    scroller.clientWidth - scrollPadding.left - scrollPadding.right
  scroller.scrollBy({
    left:
      (direction === "start" ? -1 : 1) *
      Math.max(widthBetweenFades, scroller.clientWidth / 2),
    behavior: allowsMotion() ? "smooth" : "auto"
  })
}

// The browser's own scroll on focus doesn't reliably honor `scroll-padding`,
// so the focused item is scrolled into view again explicitly.
function scrollFocusedItemIntoView(event: React.FocusEvent<HTMLElement>) {
  if (event.target === event.currentTarget) return
  if (!event.target.matches(":focus-visible")) return
  event.target.scrollIntoView({ block: "nearest", inline: "nearest" })
}

// =============================================================================
// Hook
// =============================================================================

/**
 * The overflow behavior shared by `TabLinks` and `Tabs`. Spread
 * `scrollerProps` on the element with `tabsBaseListStyles`, and render
 * `TabsBaseScrollButtons` with `scrollButtonsProps` beside it, inside an
 * element with `tabsBaseContainerStyles`.
 *
 * The item carrying `activeAttribute` is scrolled clear of the fades on mount
 * and whenever the attribute moves to another item. Vertical lists don't
 * scroll, so they never report overflow and get no scroll buttons.
 */
export function useTabsBaseOverflowScroll<T extends HTMLElement>(
  activeAttribute: "aria-current" | "data-active",
  orientation: TabsBaseOrientation
) {
  const scrollerRef = useRef<T>(null)
  const isHorizontal = orientation === "horizontal"
  // The ref stays attached in both orientations because the tracker reads it
  // only on mount, so a list that starts vertical still measures once horizontal
  const trackedOverflow = useTrackScrollOverflow(scrollerRef)
  const hasLeftOverflow = isHorizontal && trackedOverflow.hasLeftOverflow
  const hasRightOverflow = isHorizontal && trackedOverflow.hasRightOverflow

  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    const scrollActiveItemClearOfFades = () => {
      const activeItem = scroller.querySelector(`[${activeAttribute}]`)
      if (activeItem) scrollItemClearOfFades(scroller, activeItem)
    }
    scrollActiveItemClearOfFades()

    // Base UI moves `data-active` between tabs without re-rendering the
    // scroller, so watch the attribute rather than a render dependency
    const observer = new MutationObserver(scrollActiveItemClearOfFades)
    observer.observe(scroller, {
      attributeFilter: [activeAttribute],
      subtree: true
    })
    return () => observer.disconnect()
  }, [activeAttribute, orientation])

  return {
    scrollerProps: {
      ref: scrollerRef,
      "data-overflow-left": hasLeftOverflow ? "" : undefined,
      "data-overflow-right": hasRightOverflow ? "" : undefined,
      onFocus: isHorizontal ? scrollFocusedItemIntoView : undefined
    },
    scrollButtonsProps: { scrollerRef, hasLeftOverflow, hasRightOverflow }
  }
}

// =============================================================================
// Component
// =============================================================================

export type TabsBaseScrollButtonsProps = {
  scrollerRef: React.RefObject<HTMLElement>
  hasLeftOverflow: boolean
  hasRightOverflow: boolean
  "data-slot": string
}

/**
 * Buttons at each overflowing edge that scroll the items by a page for pointer
 * users. Keyboard users reach every item with Tab or the arrow keys, so the
 * buttons are hidden from assistive tech and the tab order.
 */
export function TabsBaseScrollButtons({
  scrollerRef,
  hasLeftOverflow,
  hasRightOverflow,
  "data-slot": dataSlot
}: TabsBaseScrollButtonsProps) {
  const renderScrollButton = (direction: ScrollDirection) => (
    <div
      className={cn(
        tabsBaseScrollButtonContainerStyles,
        direction === "start" ? "left-0" : "right-0"
      )}
    >
      <Button
        aria-hidden
        tabIndex={-1}
        variant="tertiary"
        size="small"
        telemetry={false}
        data-slot={dataSlot}
        // Keep focus where it was rather than on a button hidden from
        // assistive tech, which unmounts once there's nothing left to scroll
        onMouseDown={event => event.preventDefault()}
        onClick={() =>
          scrollerRef.current && scrollByPage(scrollerRef.current, direction)
        }
      >
        <Icon name={direction === "start" ? "left" : "right"} size="xs" />
      </Button>
    </div>
  )

  return (
    <>
      {hasLeftOverflow && renderScrollButton("start")}
      {hasRightOverflow && renderScrollButton("end")}
    </>
  )
}
